import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppFloat from '@/components/feature/WhatsAppFloat';
import LazyImage from '@/components/base/LazyImage';
import { blogPosts } from '@/mocks/blog';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';
import VisitaCTAForm from './components/VisitaCTAForm';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const post = blogPosts.find(p => p.slug === slug);
  const currentIndex = blogPosts.findIndex(p => p.slug === slug);
  const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;
  const related = blogPosts.filter(p => p.slug !== slug && p.category === post?.category).slice(0, 3);

  useSEO({
    title: post ? `${post.title} | Blog Home Office Premium` : 'Blog | Home Office Premium',
    description: post ? post.excerpt.slice(0, 160) : '',
    keywords: post ? post.tags.join(', ') : '',
    path: post ? `/blog/${post.slug}` : '/blog',
    ogTitle: post ? `${post.title} | Home Office Premium` : 'Blog | Home Office Premium',
    ogDescription: post ? post.excerpt.slice(0, 160) : '',
    ogImage: post?.coverImage,
  });

  useEffect(() => {
    if (!post) {
      navigate('/blog');
      return;
    }
    window.scrollTo(0, 0);
  }, [slug, post, navigate]);

  useSchemaLD({
    id: 'schema-blog-post',
    schema: post ? {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: post.excerpt,
      image: post.coverImage,
      datePublished: post.date,
      dateModified: post.date,
      author: {
        '@type': 'Organization',
        name: post.author,
        url: import.meta.env.VITE_SITE_URL ?? '',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Home Office Premium',
        url: import.meta.env.VITE_SITE_URL ?? '',
      },
      url: `${import.meta.env.VITE_SITE_URL ?? ''}/blog/${post.slug}`,
      keywords: post.tags.join(', '),
      articleSection: post.category,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: import.meta.env.VITE_SITE_URL ?? '' },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: `${import.meta.env.VITE_SITE_URL ?? ''}/blog` },
          { '@type': 'ListItem', position: 3, name: post.title, item: `${import.meta.env.VITE_SITE_URL ?? ''}/blog/${post.slug}` },
        ],
      },
    } : {},
  });

  if (!post) return null;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <WhatsAppFloat />

      {/* Cover Image */}
      <div className="w-full h-[60vh] md:h-[70vh] min-h-[420px] relative overflow-hidden">
        <LazyImage
          src={post.coverImage}
          alt={post.title}
          eager
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/50 via-foreground-950/20 to-foreground-950/70"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-14">
          <div className="max-w-[840px] mx-auto">
            <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 text-white/80 text-[11px] font-medium px-3 py-1.5 rounded-full mb-4 uppercase tracking-[0.15em] backdrop-blur-sm">
              {post.category}
            </span>
            <h1 className="text-[28px] md:text-[44px] font-semibold text-white leading-[1.1] tracking-tight">
              {post.title}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-12">

          {/* Main Content */}
          <article className="min-w-0">
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-5 text-sm text-foreground-400 mb-8 pb-8 border-b border-background-200/70">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line text-primary-600"></i>
                {post.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-primary-600"></i>
                {formatDate(post.date)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-primary-600"></i>
                {post.readTime} min de leitura
              </span>
            </div>

            {/* Article Body */}
            <div
              className="prose-content text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* CTA Visita Técnica */}
            <VisitaCTAForm />

            {/* Tags */}
            <div className="mt-12 pt-8 border-t border-background-200/70">
              <p className="text-[11px] font-semibold text-foreground-400 uppercase tracking-[0.2em] mb-3">Tags</p>
              <div className="flex flex-wrap gap-2">
                {post.tags.map(tag => (
                  <span key={tag} className="bg-background-100 text-foreground-600 text-xs px-3 py-1.5 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Prev / Next */}
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {prevPost ? (
                <Link
                  to={`/blog/${prevPost.slug}`}
                  className="group flex items-center gap-4 p-5 rounded-2xl bg-background-50 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-background-100 rounded-full group-hover:bg-primary-50 transition-colors flex-shrink-0">
                    <i className="ri-arrow-left-line text-foreground-500 group-hover:text-primary-600"></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-foreground-400 mb-0.5">Anterior</p>
                    <p className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-1">{prevPost.title}</p>
                  </div>
                </Link>
              ) : <div />}

              {nextPost ? (
                <Link
                  to={`/blog/${nextPost.slug}`}
                  className="group flex items-center gap-4 p-5 rounded-2xl bg-background-50 hover:bg-background-100 transition-colors cursor-pointer text-right justify-end"
                >
                  <div className="min-w-0">
                    <p className="text-xs text-foreground-400 mb-0.5">Próximo</p>
                    <p className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-1">{nextPost.title}</p>
                  </div>
                  <div className="w-10 h-10 flex items-center justify-center bg-background-100 rounded-full group-hover:bg-primary-50 transition-colors flex-shrink-0">
                    <i className="ri-arrow-right-line text-foreground-500 group-hover:text-primary-600"></i>
                  </div>
                </Link>
              ) : <div />}
            </div>
          </article>

          {/* Sidebar */}
          <aside className="space-y-10">
            {/* About */}
            <div className="bg-background-50 rounded-2xl p-6">
              <h4 className="text-[11px] font-semibold text-foreground-900 uppercase tracking-[0.2em] mb-4">
                Sobre nós
              </h4>
              <img
                src="/media/marca/d80a63e2-93d1-4226-9f6e-320a91838de0_horizontal-cinza.png"
                alt="Home Office Premium"
                className="h-10 w-auto object-contain mb-4"
              />
              <p className="text-foreground-500 text-sm leading-relaxed font-light">
                Referência em mobiliário corporativo em Pernambuco. Atendemos empresas de todos os portes com projetos personalizados e as melhores marcas do mercado.
              </p>
              <Link
                to="/contato"
                className="mt-5 inline-flex items-center gap-2 text-primary-600 text-sm font-medium hover:gap-3 transition-all whitespace-nowrap cursor-pointer"
              >
                Solicitar Orçamento
                <i className="ri-arrow-right-line text-sm"></i>
              </Link>
            </div>

            {/* Categories */}
            <div>
              <h4 className="text-[11px] font-semibold text-foreground-900 uppercase tracking-[0.2em] mb-4">
                Categorias
              </h4>
              <ul className="space-y-1">
                {['Ergonomia', 'Escritório Corporativo', 'Tendências', 'Produtividade', 'Sustentabilidade'].map(cat => (
                  <li key={cat}>
                    <Link
                      to="/blog"
                      className="flex items-center justify-between text-sm text-foreground-600 hover:text-primary-600 transition-colors py-1.5 cursor-pointer"
                    >
                      <span>{cat}</span>
                      <span className="text-xs text-foreground-400">
                        {blogPosts.filter(p => p.category === cat).length}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Recent Posts */}
            <div>
              <h4 className="text-[11px] font-semibold text-foreground-900 uppercase tracking-[0.2em] mb-4">
                Artigos Recentes
              </h4>
              <div className="space-y-5">
                {blogPosts.filter(p => p.slug !== slug).slice(0, 4).map(p => (
                  <Link
                    key={p.id}
                    to={`/blog/${p.slug}`}
                    className="group flex gap-3 cursor-pointer"
                  >
                    <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-background-100">
                      <LazyImage src={p.coverImage} alt={p.title} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                        {p.title}
                      </p>
                      <p className="text-xs text-foreground-400 mt-1">{formatDate(p.date)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>

        {/* Related Posts */}
        {related.length > 0 && (
          <div className="mt-20 pt-12 border-t border-background-200/70">
            <h3 className="text-[22px] md:text-[28px] font-semibold text-foreground-900 mb-8">
              Artigos Relacionados
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {related.map(p => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  className="group cursor-pointer"
                >
                  <div className="img-zoom rounded-2xl aspect-[16/10] bg-background-100">
                    <LazyImage src={p.coverImage} alt={p.title} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="pt-4">
                    <span className="text-primary-600 text-[11px] font-semibold uppercase tracking-[0.15em]">{p.category}</span>
                    <h4 className="mt-2 text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                      {p.title}
                    </h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />

      <style>{`
        .prose-content h2 {
          font-size: 1.75rem;
          font-weight: 600;
          letter-spacing: -0.02em;
          color: #1C1917;
          margin-top: 2.5rem;
          margin-bottom: 0.75rem;
        }
        .prose-content h3 {
          font-size: 1.3rem;
          font-weight: 600;
          letter-spacing: -0.02em;
          color: #292524;
          margin-top: 2rem;
          margin-bottom: 0.5rem;
        }
        .prose-content p {
          margin-bottom: 1.25rem;
          font-size: 1.05rem;
          line-height: 1.85;
          color: #57534E;
          font-weight: 300;
        }
        .prose-content strong {
          color: #1C1917;
          font-weight: 600;
        }
        .prose-content ul {
          list-style: none;
          padding-left: 0;
          margin-bottom: 1.25rem;
        }
        .prose-content ul li {
          position: relative;
          padding-left: 1.5rem;
          margin-bottom: 0.6rem;
          color: #57534E;
          font-size: 1.05rem;
          line-height: 1.75;
          font-weight: 300;
        }
        .prose-content ul li::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0.65em;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #E8610A;
        }
      `}</style>
    </div>
  );
}